import React, { useState, useMemo } from 'react';
import { GRCControl, AIApplicabilityResult } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { EvidenceModal } from '../dashboard/EvidenceModal';
import { AIApplicabilityWizard } from './AIApplicabilityWizard';
import { CISScriptViewer } from './CISScriptViewer';
import { 
  Search, Filter, FileText, Brain, Terminal, ChevronDown, ChevronUp,
  CheckCircle, XCircle, AlertCircle, Download, Sparkles, RefreshCw,
  Settings, Eye, FileCode, Trash2, Plus
} from 'lucide-react';

interface EnhancedSoATableProps {
  controls: GRCControl[];
  frameworkId: number;
  frameworkName: string;
  frameworkType: string;
  onControlsUpdate: () => void;
}

export const EnhancedSoATable: React.FC<EnhancedSoATableProps> = ({
  controls,
  frameworkId,
  frameworkName,
  frameworkType,
  onControlsUpdate
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [applicabilityFilter, setApplicabilityFilter] = useState<'all' | 'applicable' | 'not_applicable' | 'partial'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'compliant' | 'partial' | 'non_compliant' | 'not_assessed'>('all');
  const [checkTypeFilter, setCheckTypeFilter] = useState<'all' | 'automated' | 'manual' | 'hybrid'>('all');
  const [expandedControl, setExpandedControl] = useState<number | null>(null);
  
  // Modals
  const [evidenceModal, setEvidenceModal] = useState<GRCControl | null>(null);
  const [cisScriptModal, setCisScriptModal] = useState<GRCControl | null>(null);
  const [showAIWizard, setShowAIWizard] = useState(false);
  
  const [applyingAI, setApplyingAI] = useState(false);

  const handleDeleteControl = async (controlId: number) => {
    if (!window.confirm('Are you sure you want to delete this control?')) return;
    const ok = await grcService.deleteControl(controlId);
    if (ok) onControlsUpdate();
  };

  const handleCreateControl = async () => {
    const title = window.prompt('Enter control title:');
    if (!title) return;
    const ref = window.prompt('Enter control reference (e.g., NEW-1):', 'NEW-' + Date.now());
    const result = await grcService.createControl(frameworkId, { title, control_ref: ref || undefined });
    if (result.success) onControlsUpdate();
  };

  const isCISBenchmark = frameworkType === 'benchmark' || frameworkName.toLowerCase().includes('cis');

  // Filtered controls
  const filteredControls = useMemo(() => {
    let result = controls;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.control_code.toLowerCase().includes(query) ||
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      );
    }

    // Applicability filter
    if (applicabilityFilter !== 'all') {
      const applValue = applicabilityFilter === 'applicable' ? 1 : 
                        applicabilityFilter === 'not_applicable' ? 0 : 2;
      result = result.filter(c => Number(c.applicable) === applValue);
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    // Check type filter (for CIS benchmarks)
    if (checkTypeFilter !== 'all') {
      result = result.filter(c => c.check_type === checkTypeFilter);
    }

    return result;
  }, [controls, searchQuery, applicabilityFilter, statusFilter, checkTypeFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: controls.length,
    applicable: controls.filter(c => Number(c.applicable) === 1).length,
    notApplicable: controls.filter(c => Number(c.applicable) === 0).length,
    partial: controls.filter(c => Number(c.applicable) === 2).length,
    automated: controls.filter(c => c.check_type === 'automated').length,
    manual: controls.filter(c => c.check_type === 'manual').length,
    hybrid: controls.filter(c => c.check_type === 'hybrid').length
  }), [controls]);

  const handleAIApplicabilityComplete = async (results: AIApplicabilityResult[]) => {
    setApplyingAI(true);
    try {
      // Apply AI results to controls
      for (const result of results) {
        await grcService.updateControl({
          id: result.control_id,
          applicable: result.applicable ? 1 : 0,
          applicability_justification: result.reason,
          owner: result.suggested_owner || null
        });
      }
      onControlsUpdate();
    } catch (error) {
      console.error('Error applying AI results:', error);
    }
    setApplyingAI(false);
    setShowAIWizard(false);
  };

  const exportToCSV = () => {
    const headers = ['Control Code', 'Title', 'Applicable', 'Status', 'Check Type', 'Owner', 'Evidence Count'];
    const rows = filteredControls.map(c => [
      c.control_code,
      c.title,
      Number(c.applicable) === 1 ? 'Yes' : Number(c.applicable) === 0 ? 'No' : 'Partial',
      c.status,
      c.check_type,
      c.owner || '',
      c.evidence_count || 0
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soa_${frameworkName.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs text-gray-500">Total</div>
        </div>
        <div 
          onClick={() => setApplicabilityFilter(applicabilityFilter === 'applicable' ? 'all' : 'applicable')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
            applicabilityFilter === 'applicable' 
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
              : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
          }`}
        >
          <div className="text-2xl font-bold text-green-600">{stats.applicable}</div>
          <div className="text-xs text-gray-500">Applicable</div>
        </div>
        <div 
          onClick={() => setApplicabilityFilter(applicabilityFilter === 'not_applicable' ? 'all' : 'not_applicable')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
            applicabilityFilter === 'not_applicable' 
              ? 'border-gray-500 bg-gray-100 dark:bg-gray-700' 
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
          }`}
        >
          <div className="text-2xl font-bold text-gray-500">{stats.notApplicable}</div>
          <div className="text-xs text-gray-500">Not Applicable</div>
        </div>
        <div 
          onClick={() => setApplicabilityFilter(applicabilityFilter === 'partial' ? 'all' : 'partial')}
          className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
            applicabilityFilter === 'partial' 
              ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
              : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
          }`}
        >
          <div className="text-2xl font-bold text-amber-600">{stats.partial}</div>
          <div className="text-xs text-gray-500">Partial</div>
        </div>
        
        {isCISBenchmark && (
          <>
            <div 
              onClick={() => setCheckTypeFilter(checkTypeFilter === 'automated' ? 'all' : 'automated')}
              className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
                checkTypeFilter === 'automated' 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
              }`}
            >
              <div className="text-2xl font-bold text-blue-600">{stats.automated}</div>
              <div className="text-xs text-gray-500">Automated</div>
            </div>
            <div 
              onClick={() => setCheckTypeFilter(checkTypeFilter === 'manual' ? 'all' : 'manual')}
              className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
                checkTypeFilter === 'manual' 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
              }`}
            >
              <div className="text-2xl font-bold text-purple-600">{stats.manual}</div>
              <div className="text-xs text-gray-500">Manual</div>
            </div>
            <div 
              onClick={() => setCheckTypeFilter(checkTypeFilter === 'hybrid' ? 'all' : 'hybrid')}
              className={`bg-white dark:bg-gray-800 rounded-lg p-3 border cursor-pointer transition-colors ${
                checkTypeFilter === 'hybrid' 
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
              }`}
            >
              <div className="text-2xl font-bold text-indigo-600">{stats.hybrid}</div>
              <div className="text-xs text-gray-500">Hybrid</div>
            </div>
          </>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search controls..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="compliant">Compliant</option>
            <option value="partial">Partial</option>
            <option value="non_compliant">Non-Compliant</option>
            <option value="not_assessed">Not Assessed</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          {/* Add Control */}
          <button
            onClick={handleCreateControl}
            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Control
          </button>

          {/* AI Applicability Button */}
          <button
            onClick={() => setShowAIWizard(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-indigo-700 flex items-center gap-2"
          >
            <Brain className="w-4 h-4" />
            AI Applicability
          </button>

          {/* Export */}
          <button
            onClick={exportToCSV}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {(applicabilityFilter !== 'all' || statusFilter !== 'all' || checkTypeFilter !== 'all') && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Active filters:</span>
          {applicabilityFilter !== 'all' && (
            <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded flex items-center gap-1">
              {applicabilityFilter.replace('_', ' ')}
              <button onClick={() => setApplicabilityFilter('all')} className="hover:text-indigo-900">×</button>
            </span>
          )}
          {statusFilter !== 'all' && (
            <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded flex items-center gap-1">
              {statusFilter.replace('_', ' ')}
              <button onClick={() => setStatusFilter('all')} className="hover:text-indigo-900">×</button>
            </span>
          )}
          {checkTypeFilter !== 'all' && (
            <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded flex items-center gap-1">
              {checkTypeFilter}
              <button onClick={() => setCheckTypeFilter('all')} className="hover:text-indigo-900">×</button>
            </span>
          )}
          <button 
            onClick={() => {
              setApplicabilityFilter('all');
              setStatusFilter('all');
              setCheckTypeFilter('all');
            }}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Control</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Title</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Applicable</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th>
                {isCISBenchmark && (
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Check Type</th>
                )}
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Evidence</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredControls.map(control => (
                <React.Fragment key={control.id}>
                  <tr 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      expandedControl === control.id ? 'bg-indigo-50 dark:bg-indigo-900/10' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => setExpandedControl(expandedControl === control.id ? null : control.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedControl === control.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {control.control_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                      {control.title}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`
                        inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                        ${control.applicable === 1 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                        ${control.applicable === 0 ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' : ''}
                        ${control.applicable === 2 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : ''}
                      `}>
                        {control.applicable === 1 && <CheckCircle className="w-3 h-3" />}
                        {control.applicable === 0 && <XCircle className="w-3 h-3" />}
                        {control.applicable === 2 && <AlertCircle className="w-3 h-3" />}
                        {control.applicable === 1 ? 'Yes' : control.applicable === 0 ? 'No' : 'Partial'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-semibold capitalize
                        ${control.status === 'compliant' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                        ${control.status === 'partial' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : ''}
                        ${control.status === 'non_compliant' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : ''}
                        ${control.status === 'not_assessed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' : ''}
                      `}>
                        {control.status?.replace('_', ' ') || 'N/A'}
                      </span>
                    </td>
                    {isCISBenchmark && (
                      <td className="px-4 py-3 text-center">
                        <span className={`
                          inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                          ${control.check_type === 'automated' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : ''}
                          ${control.check_type === 'manual' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : ''}
                          ${control.check_type === 'hybrid' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : ''}
                        `}>
                          {control.check_type === 'automated' && <Terminal className="w-3 h-3" />}
                          {control.check_type === 'manual' && <Eye className="w-3 h-3" />}
                          {control.check_type === 'hybrid' && <Settings className="w-3 h-3" />}
                          {control.check_type}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setEvidenceModal(control)}
                        className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 mx-auto ${
                          control.evidence_count > 0
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        <FileText className="w-3 h-3" />
                        {control.evidence_count || 0}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setEvidenceModal(control)}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded"
                          title="Add Evidence"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {isCISBenchmark && (
                          <button
                            onClick={() => setCisScriptModal(control)}
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                            title="View Check Script"
                          >
                            <Terminal className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteControl(control.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                          title="Delete Control"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {expandedControl === control.id && (
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <td colSpan={isCISBenchmark ? 8 : 7} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {control.description || 'No description available'}
                            </p>
                          </div>
                          <div className="space-y-3">
                            {control.applicability_justification && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Applicability Reason</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{control.applicability_justification}</p>
                              </div>
                            )}
                            {control.owner && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{control.owner}</p>
                              </div>
                            )}
                            {control.implementation_notes && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Implementation Notes</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{control.implementation_notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredControls.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No controls match your filters</p>
          </div>
        )}

        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 text-sm text-gray-500 dark:text-gray-400 flex justify-between items-center">
          <span>Showing {filteredControls.length} of {controls.length} controls</span>
          {filteredControls.length > 0 && (
            <span>
              {filteredControls.filter(c => c.applicable === 1).length} applicable, {' '}
              {filteredControls.filter(c => c.status === 'compliant').length} compliant
            </span>
          )}
        </div>
      </div>

      {/* Modals */}
      {evidenceModal && (
        <EvidenceModal
          controlId={evidenceModal.id}
          controlCode={evidenceModal.control_code}
          controlTitle={evidenceModal.title}
          onClose={() => setEvidenceModal(null)}
          onUpdate={onControlsUpdate}
        />
      )}

      {cisScriptModal && (
        <CISScriptViewer
          control={cisScriptModal}
          onClose={() => setCisScriptModal(null)}
        />
      )}

      {showAIWizard && (
        <AIApplicabilityWizard
          frameworkId={frameworkId}
          frameworkName={frameworkName}
          onComplete={handleAIApplicabilityComplete}
          onClose={() => setShowAIWizard(false)}
        />
      )}

      {/* Loading overlay for AI apply */}
      {applyingAI && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
            <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-900 dark:text-white font-medium">Applying AI Results...</p>
          </div>
        </div>
      )}
    </div>
  );
};
